import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw, Facebook, Instagram, AtSign, Youtube, Music2, Search } from 'lucide-react';
import type { ConnectionInfo, SocialAccount, SocialConnectorInfo, SocialConnectorKey } from '@unitly/contracts';
import {
  useAssignSocialAccount, useConnectorAction, useConnectors, useSocialAccounts, useSocialConnectorAction,
  useSocialConnectors, useWorkspaces,
} from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { PageLoader } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';
import { relativeTime } from '../../lib/dates.ts';
import { GoogleCalendarLogo } from '../calendar/CalendarPage.tsx';

const RESULT_MESSAGES: Record<string, string> = {
  connected: 'Google Calendar collegato',
  denied: 'Collegamento annullato: nessun permesso concesso',
  expired: 'Il collegamento è scaduto: riprova',
  error: 'Collegamento non riuscito: riprova',
};

function syncedLabel(iso: string) {
  const rel = relativeTime(iso);
  if (rel === 'ora') return 'adesso';
  return /min|h$/.test(rel) ? `${rel} fa` : rel;
}

const STATUS: Record<ConnectionInfo['status'], { label: string; color: string }> = {
  connected: { label: 'Collegato', color: 'var(--green)' },
  error: { label: 'Errore di sincronizzazione', color: 'var(--orange)' },
  expired: { label: 'Autorizzazione scaduta', color: 'var(--red)' },
  disconnected: { label: 'Scollegato', color: 'var(--label-3)' },
};

export function IntegrationsSettings() {
  const { orgId } = useOrgCtx();
  const connectors = useConnectors(orgId);
  const action = useConnectorAction(orgId);
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    const r = params.get('google');
    if (r) {
      toast(RESULT_MESSAGES[r] ?? 'Operazione completata');
      setParams((p) => { const n = new URLSearchParams(p); n.delete('google'); return n; }, { replace: true });
    }
  }, [params, setParams, toast]);

  if (connectors.isLoading || !connectors.data) return <PageLoader />;
  const google = connectors.data.find((c) => c.key === 'google_calendar')!;
  const personal = google.connections.find((c) => c.scope === 'user' && c.mine);
  const company = google.connections.find((c) => c.scope === 'organization');
  const connect = (scope: 'user' | 'organization') =>
    action.mutate({ action: 'authorize', scope }, {
      onSuccess: (r) => { if (r && 'url' in r) window.location.assign(r.url); },
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Impossibile avviare il collegamento'),
    });

  return (
    <>
      <div className="card connector-card">
        <div className="connector-head">
          <span className="connector-logo"><GoogleCalendarLogo /></span>
          <span style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 17 }}>{google.name}</div>
            <div className="hint" style={{ padding: 0 }}>{google.description}</div>
          </span>
        </div>

        {!google.configured ? (
          <div className="form-error" style={{ background: 'var(--fill)', color: 'var(--label-2)' }}>
            Non ancora disponibile: chi gestisce il server deve impostare le credenziali OAuth di Google
            (<code>GOOGLE_CLIENT_ID</code> e <code>GOOGLE_CLIENT_SECRET</code>). Le istruzioni sono in <code>docs/integrazioni/google-calendar.md</code>.
          </div>
        ) : (
          <>
            <ConnectionBlock
              title="Il tuo calendario"
              description="Solo tu vedi questi eventi in Unitly."
              connection={personal}
              onConnect={() => connect('user')}
              busy={action.isPending}
            />
            {(google.canConnectOrganization || company) && (
              <ConnectionBlock
                title="Calendario aziendale"
                description="Scegli i calendari condivisi (es. “Globaly”) da mostrare a tutto il team. Gli eventi privati appaiono come “Occupato”."
                connection={company}
                onConnect={google.canConnectOrganization ? () => connect('organization') : undefined}
                busy={action.isPending}
              />
            )}
          </>
        )}
      </div>
      <p className="hint" style={{ marginTop: 12 }}>
        Unitly legge i calendari in sola lettura. I token sono cifrati sul server e non arrivano mai al browser; scollegando, l’autorizzazione viene revocata anche su Google.
      </p>
    </>
  );
}

export function ConnectionBlock({ title, description, connection, onConnect, busy }: {
  title: string; description: string; connection?: ConnectionInfo; onConnect?: () => void; busy: boolean;
}) {
  const { orgId } = useOrgCtx();
  const action = useConnectorAction(orgId);
  const toast = useToast();
  const status = connection ? STATUS[connection.status] : null;
  const selected = connection?.calendars.filter((c) => c.selected).map((c) => c.id) ?? [];

  return (
    <div style={{ borderTop: '0.5px solid var(--separator)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontWeight: 600 }}>{title}</div>
          <div className="hint" style={{ padding: 0 }}>{connection?.accountLabel ?? description}</div>
        </span>
        {status && <span className="status-pill" style={{ '--sp': status.color } as React.CSSProperties}>{status.label}</span>}
        {!connection && onConnect && <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={onConnect}>Collega Google</button>}
        {connection?.status === 'expired' && onConnect && <button type="button" className="btn btn-primary btn-sm" onClick={onConnect}>Ricollega</button>}
      </div>

      {connection && (
        <>
          {connection.lastError && <div className="form-error">{connection.lastError}</div>}
          <div>
            {connection.calendars.map((c) => (
              <label key={c.id} className="cal-toggle">
                <span className="swatch" style={{ '--sw': c.color ?? 'var(--blue)' } as React.CSSProperties} />
                <span style={{ flex: 1 }}>{c.name}{c.primary ? ' (principale)' : ''}</span>
                <button type="button" role="switch" className="switch" aria-checked={c.selected} aria-label={`Mostra ${c.name}`} disabled={!connection.canManage || action.isPending}
                  onClick={() => action.mutate(
                    { action: 'select', id: connection.id, calendarIds: c.selected ? selected.filter((x) => x !== c.id) : [...selected, c.id] },
                    { onError: (e) => toast(e.message) },
                  )} />
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span className="hint" style={{ padding: 0, flex: 1 }}>
              {connection.lastSyncAt ? `Ultima sincronizzazione: ${syncedLabel(connection.lastSyncAt)}` : 'Mai sincronizzato'} · automatica ogni 10 minuti
            </span>
            {connection.canManage && (
              <>
                <button type="button" className="btn btn-secondary btn-sm" disabled={action.isPending}
                  onClick={() => action.mutate({ action: 'sync', id: connection.id }, { onSuccess: () => toast('Sincronizzato'), onError: (e) => toast(e.message) })}>
                  <RefreshCw size={15} /> Sincronizza ora
                </button>
                <button type="button" className="btn btn-danger btn-sm"
                  onClick={() => confirm('Scollegare Google Calendar? Gli eventi sincronizzati spariranno da Unitly.') &&
                    action.mutate({ action: 'disconnect', id: connection.id }, { onSuccess: () => toast('Google Calendar scollegato') })}>
                  Scollega
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ social (Meta/Threads/YouTube/TikTok)

const SOCIAL_RESULT_MESSAGES: Record<string, string> = {
  connected: 'Account collegato',
  denied: 'Collegamento annullato: nessun permesso concesso',
  expired: 'Il collegamento è scaduto: riprova',
  error: 'Collegamento non riuscito: riprova',
  no_channel: 'Questo account Google non ha un canale YouTube: accedi con l’account che gestisce il canale',
};

const SOCIAL_STATUS: Record<ConnectionInfo['status'], { label: string; color: string }> = {
  connected: { label: 'Collegato', color: 'var(--green)' },
  error: { label: 'Errore', color: 'var(--orange)' },
  expired: { label: 'Autorizzazione scaduta', color: 'var(--red)' },
  disconnected: { label: 'Scollegato', color: 'var(--label-3)' },
};

const PLATFORM_ICON: Record<SocialAccount['platform'], typeof Facebook> = {
  facebook: Facebook, instagram: Instagram, threads: AtSign, youtube: Youtube, tiktok: Music2,
};
const PLATFORM_LABEL: Record<SocialAccount['platform'], string> = {
  facebook: 'Facebook', instagram: 'Instagram', threads: 'Threads', youtube: 'YouTube', tiktok: 'TikTok',
};

const PLATFORM_ORDER: SocialAccount['platform'][] = ['facebook', 'instagram', 'threads', 'youtube', 'tiktok'];

/** Company accounts → workspace, grouped by platform, with a search and platform filter: a company with
 * dozens of Pages and profiles needs to find one quickly, not scroll a single mixed list. */
function AccountAssignments({ accounts, workspaces, isOrgAdmin, onAssign }: {
  accounts: SocialAccount[];
  workspaces: { id: string; name: string }[];
  isOrgAdmin: boolean;
  onAssign: (a: SocialAccount, workspaceId: string | null) => void;
}) {
  const [query, setQuery] = useState('');
  const [platform, setPlatform] = useState<SocialAccount['platform'] | 'all'>('all');
  const [onlyFree, setOnlyFree] = useState(false);
  const q = query.trim().toLocaleLowerCase('it');
  const matches = (a: SocialAccount) =>
    (!onlyFree || !a.assignedWorkspaceId) &&
    (!q || a.name.toLocaleLowerCase('it').includes(q) || (a.assignedWorkspaceName ?? '').toLocaleLowerCase('it').includes(q));
  const present = PLATFORM_ORDER.filter((p) => accounts.some((a) => a.platform === p));
  const groups = present
    .filter((p) => platform === 'all' || platform === p)
    .map((p) => ({ platform: p, all: accounts.filter((a) => a.platform === p), shown: accounts.filter((a) => a.platform === p && matches(a)) }))
    .filter((g) => g.shown.length > 0);

  return (
    <>
      <h3 className="subhead">Assegna gli account agli spazi</h3>
      <p className="hint" style={{ marginTop: 0 }}>Ogni account può essere usato da un solo spazio alla volta.</p>
      <div style={{ position: 'relative', marginBottom: 10 }}>
        <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--label-3)', pointerEvents: 'none' }} />
        <input className="input" type="search" placeholder="Cerca un account o uno spazio…" aria-label="Cerca account social"
          value={query} onChange={(e) => setQuery(e.target.value)} style={{ paddingLeft: 36 }} />
      </div>
      <div className="chips" role="group" aria-label="Piattaforma" style={{ flexWrap: 'wrap', overflow: 'visible', margin: '0 0 14px', padding: 0 }}>
        <button type="button" className="chip" aria-pressed={platform === 'all'} onClick={() => setPlatform('all')}>Tutte · {accounts.length}</button>
        {present.map((p) => {
          const Icon = PLATFORM_ICON[p];
          return (
            <button key={p} type="button" className="chip" aria-pressed={platform === p} onClick={() => setPlatform(p)}>
              <Icon size={14} /> {PLATFORM_LABEL[p]} · {accounts.filter((a) => a.platform === p).length}
            </button>
          );
        })}
        <button type="button" className="chip" aria-pressed={onlyFree} onClick={() => setOnlyFree(!onlyFree)}>Solo non assegnati</button>
      </div>
      {groups.length === 0 && <div className="card"><p className="hint" style={{ padding: 16 }}>Nessun account corrisponde alla ricerca.</p></div>}
      {groups.map((g) => {
        const Icon = PLATFORM_ICON[g.platform];
        const assigned = g.all.filter((a) => a.assignedWorkspaceId).length;
        return (
          <section key={g.platform} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 4px 6px' }}>
              <Icon size={16} />
              <span style={{ fontWeight: 600 }}>{PLATFORM_LABEL[g.platform]}</span>
              <span className="hint" style={{ padding: 0 }}>{assigned} di {g.all.length} assegnati</span>
            </div>
            <div className="card list">
              {g.shown.map((a) => (
                <div key={`${a.connectionId}:${a.platform}:${a.pageId ?? ''}`} className="list-row">
                  <span className="grow">{a.name}</span>
                  {isOrgAdmin ? (
                    <select
                      className="select" style={{ width: 'auto', maxWidth: '55%', minHeight: 32, padding: '4px 30px 4px 10px', fontSize: 14 }}
                      value={a.assignedWorkspaceId ?? ''}
                      aria-label={`Spazio per ${a.name}`}
                      onChange={(e) => onAssign(a, e.target.value || null)}
                    >
                      <option value="">Nessuno spazio</option>
                      {workspaces.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                  ) : (
                    <span className="pill">{a.assignedWorkspaceName ?? 'Non assegnato'}</span>
                  )}
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

/** Every Meta/Threads/YouTube/TikTok connector, collegato once for the whole company - this is the only
 * place accounts get connected. For each connected Page/Instagram account/Threads profile, pick which
 * single workspace uses it (see account-assignments.ts on the backend). */
/** YouTube and TikTok give an app one channel/account per sign-in (for YouTube, the Google account or
 * Brand Account picked on Google's screen), so each is its own company connection: listed one per line,
 * plus "Aggiungi canale"/"Aggiungi account". */
function MultiAccountRow({ c, first, isOrgAdmin, busy, onConnect }: {
  c: SocialConnectorInfo; first: boolean; isOrgAdmin: boolean; busy: boolean; onConnect: () => void;
}) {
  const connectorAction = useSocialConnectorAction(useOrgCtx().orgId);
  const toast = useToast();
  const channels = c.connections.filter((x) => x.scope === 'organization');
  const Icon = c.key === 'tiktok' ? Music2 : Youtube;
  const noun = c.key === 'tiktok' ? 'account' : 'canale';
  return (
    <div style={{ borderTop: first ? 'none' : '0.5px solid var(--separator)', paddingTop: first ? 0 : 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontWeight: 600 }}>{c.name}</div>
          <div className="hint" style={{ padding: 0 }}>
            {!channels.length ? c.description
              : c.key === 'tiktok' ? 'Un collegamento per account: su TikTok accedi con l’account da aggiungere.'
              : 'Un collegamento per canale: su Google scegli l’account o il canale (Brand Account) da aggiungere.'}
          </div>
        </span>
        {!c.configured && <span className="hint" style={{ padding: 0 }}>Non ancora disponibile su questo server.</span>}
        {c.configured && isOrgAdmin && (
          <button type="button" className={`btn btn-sm ${channels.length ? 'btn-plain' : 'btn-primary'}`} disabled={busy} onClick={onConnect}>
            {channels.length ? `Aggiungi ${noun}` : 'Collega'}
          </button>
        )}
      </div>
      {channels.map((ch) => {
        const status = SOCIAL_STATUS[ch.status];
        return (
          <div key={ch.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingLeft: 12 }}>
            <Icon size={15} style={{ opacity: 0.7, flex: 'none' }} />
            <span style={{ flex: 1, minWidth: 140, fontSize: 14 }}>{ch.accountLabel ?? c.name}</span>
            {status && <span className="status-pill" style={{ '--sp': status.color } as React.CSSProperties}>{status.label}</span>}
            {c.configured && isOrgAdmin && ch.status === 'expired' && (
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={onConnect}>Ricollega</button>
            )}
            {ch.canManage && (
              <button type="button" className="btn btn-plain btn-sm" style={{ color: 'var(--red)' }}
                onClick={() => confirm(`Scollegare ${ch.accountLabel ?? c.name}? Lo spazio che lo usa perderà l’accesso.`) &&
                  connectorAction.mutate({ action: 'disconnect', id: ch.id }, { onSuccess: () => toast(`${c.name} scollegato`) })}>
                Scollega
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function SocialIntegrationsSettings() {
  const { orgId, detail } = useOrgCtx();
  const isOrgAdmin = Boolean(detail?.capabilities.admin);
  const connectors = useSocialConnectors(orgId);
  const connectorAction = useSocialConnectorAction(orgId);
  const accounts = useSocialAccounts(orgId);
  const workspaces = useWorkspaces(orgId);
  const assign = useAssignSocialAccount(orgId);
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    for (const key of ['meta', 'threads', 'youtube', 'tiktok'] as const) {
      const r = params.get(key);
      if (r) {
        toast(SOCIAL_RESULT_MESSAGES[r] ?? 'Operazione completata');
        setParams((p) => { const n = new URLSearchParams(p); n.delete(key); return n; }, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (connectors.isLoading || !connectors.data) return <PageLoader />;

  const connect = (key: SocialConnectorKey) =>
    connectorAction.mutate({ action: 'authorize', key, scope: 'organization' }, {
      onSuccess: (r) => { if (r && 'url' in r) window.location.assign(r.url); },
      onError: (e) => toast(e instanceof ApiError ? e.message : 'Impossibile avviare il collegamento'),
    });

  return (
    <>
      <h3 className="subhead">Account social</h3>
      <div className="card connector-card" style={{ marginBottom: 16 }}>
        {connectors.data.map((c, i) => {
          if (c.key === 'youtube' || c.key === 'tiktok') return <MultiAccountRow key={c.key} c={c} first={i === 0} isOrgAdmin={isOrgAdmin} busy={connectorAction.isPending} onConnect={() => connect(c.key)} />;
          const company = c.connections.find((x) => x.scope === 'organization');
          const status = company ? SOCIAL_STATUS[company.status] : null;
          return (
            <div key={c.key} style={{ borderTop: i === 0 ? 'none' : '0.5px solid var(--separator)', paddingTop: i === 0 ? 0 : 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontWeight: 600 }}>{c.name}</div>
                  <div className="hint" style={{ padding: 0 }}>{company?.accountLabel ?? c.description}</div>
                </span>
                {status && <span className="status-pill" style={{ '--sp': status.color } as React.CSSProperties}>{status.label}</span>}
                {!c.configured && <span className="hint" style={{ padding: 0 }}>Non ancora disponibile su questo server.</span>}
                {c.configured && isOrgAdmin && !company && (
                  <button type="button" className="btn btn-primary btn-sm" disabled={connectorAction.isPending} onClick={() => connect(c.key)}>Collega</button>
                )}
                {c.configured && isOrgAdmin && company && (
                  // Also when connected: new Meta permissions only reach Unitly with a fresh consent. Same connection, assignments kept.
                  <button type="button" className={`btn btn-sm ${company.status === 'expired' ? 'btn-primary' : 'btn-plain'}`}
                    disabled={connectorAction.isPending} onClick={() => connect(c.key)}>Ricollega</button>
                )}
                {company?.canManage && (
                  <button type="button" className="btn btn-plain btn-sm" style={{ color: 'var(--red)' }}
                    onClick={() => confirm(`Scollegare ${c.name}? Gli spazi che lo usano perderanno l’accesso.`) &&
                      connectorAction.mutate({ action: 'disconnect', id: company.id }, { onSuccess: () => toast(`${c.name} scollegato`) })}>
                    Scollega
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {accounts.data && accounts.data.length > 0 && (
        <AccountAssignments
          accounts={accounts.data} workspaces={workspaces.data ?? []} isOrgAdmin={isOrgAdmin}
          onAssign={(a, workspaceId) => assign.mutate(
            { connectionId: a.connectionId, platform: a.platform, pageId: a.pageId, workspaceId },
            { onError: (err) => toast(err instanceof ApiError ? err.message : 'Assegnazione non riuscita') },
          )}
        />
      )}
    </>
  );
}
